import React, { useRef, useState, useEffect } from 'react';
import { Modal } from '@/components/ui';
import { X, Download } from 'lucide-react';
import { getInvoiceTemplate } from './invoiceTemplate';
import { useCompanyInfo } from '@/pages/verve-settings/hooks/useCompanyInfo';
import { settingsTermService } from '@/pages/verve-settings/services/settingsTermService';

export const InvoicePreviewModal = ({ isOpen, onClose, sale }) => {
  const { company } = useCompanyInfo();
  const invoiceRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [termsHtml, setTermsHtml] = useState("");

  useEffect(() => {
    if (isOpen) {
      settingsTermService.getTerms()
        .then(res => {
          if (res?.success) {
            const activeTerms = res.data.filter(t => t.status === 'Active' && t.category === 'Sale Invoice');
            if (activeTerms.length > 0) {
              const html = activeTerms.map((t, i) => `
                <div style="margin-bottom: ${i === activeTerms.length - 1 ? '0' : '6px'};">
                  ${t.description ? `<div style="font-weight: 600; margin-bottom: 2px;">${t.description}</div>` : ''}
                  ${t.paymentDescription ? `<div>${t.paymentDescription}</div>` : ''}
                </div>
              `).join('');
              setTermsHtml(html);
            }
          }
        })
        .catch(err => console.error("Failed to load terms:", err));
    }
  }, [isOpen]);

  if (!isOpen || !sale) return null;

  const ci = {
    companyName:    company.name    || 'Verve Kitchens',
    companyAddress: company.address || '',
    companyEmail:   company.email   || '',
    companyPhone:   company.phone   || '',
    companyGst:     company.gst     || '',
    logoUrl:        company.logoUrl || '',
    digitalSignatureUrl: company.digitalSignatureUrl || '',
  };

  const html = getInvoiceTemplate(sale, ci, termsHtml);

  const handleDownload = async () => {
    if (!invoiceRef.current) return;
    setDownloading(true);
    try {
      const html2pdf = (await import('html2pdf.js/dist/html2pdf.bundle.min.js')).default;
      const filename = `Invoice-${sale.invoiceNo || sale._id || 'download'}.pdf`;
      const opt = {
        margin:       [4, 4, 4, 4],
        filename,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true, allowTaint: true },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };
      await html2pdf().set(opt).from(invoiceRef.current).save();
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-[850px] !p-0 overflow-hidden print:max-w-full print:m-0 print:border-none print:shadow-none bg-slate-100"
      hideCloseButton={true}
    >
      {/* Action Bar */}
      <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-widest">
          Invoice Preview
        </h2>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDownload}
            disabled={downloading}
            style={{ backgroundColor: '#2b448f', color: '#ffffff' }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg hover:opacity-90 transition-opacity shadow disabled:opacity-60"
          >
            <Download size={16} />
            {downloading ? 'Downloading...' : 'Download PDF'}
          </button>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Invoice Document */}
      <div className="p-6 bg-slate-100 overflow-auto">
        <div
          ref={invoiceRef}
          className="mx-auto"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </Modal>
  );
};
